import { Flex, Group, Input, SegmentedControl, Select, Slider, Switch, Text } from '@byecode/ui'
import type { DateRange, FieldInputType } from '@lighthouse/core'
import { FIELD_TYPE_LIMIT_ENUM, FILE_TYPE_CATEGORY_ENUM, FilterMode } from '@lighthouse/core'
import {
    AVATAR_FILE_INPUT_LIMITS,
    defaultFileMaxUploadSize,
    getAppointField,
    getFieldOptions,
    getFileSizeToMB,
    getIsFindUseInFilter,
    getViewColumns,
    ListItem4ByecodeUi,
    notDataSettingInnerTypes,
    PURE_TEXT_INPUT,
    SelectDataSource,
    useAtomAction,
    useAtomData,
    useFindUseObjectContext,
    useSpaceQuotaContext,
    VIDEO_FILE_INPUT_LIMITS,
    ViewFieldFilterConfigure
} from '@lighthouse/shared'
import React, { useCallback, useDeferredValue, useMemo } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import styled from 'styled-components'

import {
    availableFieldBlocksAtom,
    availableFilterBlocksAtom,
    blockHighlightAtom,
    lastPageOfStackAtom,
    pagePathParameterFamily,
    pageQueryParameterFamily,
    pageStatesParameterFamily,
    pageVariableSchemasFamily
} from '@/atoms/page/state'
import { AppSmsCard } from '@/components/AppSmsCard'
import { PopoverQuestion } from '@/components/PopoverQuestion'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSource, useDataSourceList } from '@/hooks/useDataSource'

import { FieldSelect } from '../../../../FieldSelect'
import { ListItemMenu } from '../../ListItemMenu'
import { SwitchControl } from '../../SwitchControl'
import { Required } from '../../UserOperate'
import { Repeat } from '../../UserOperate/Repeat'
import { CALENDAR_RANGE_OPTIONS, DATE_RANGE_OPTIONS, FILE_LIMIT_TYPE_OPTIONS, FILE_TYPE_CATEGORY_OPTIONS } from '../constants'
import type { FieldInputFormSlice } from '../formSlice'
import type { BaseFieldInputSetting } from '../types'

interface UserProps extends BaseFieldInputSetting {}

const SCxContainer = styled.div`
    padding: 0 8px;
`

export const User: React.FunctionComponent<UserProps> = ({ mode, pointer, prefix }) => {
    const { watch, control, getValues, setValue, register } = useFormContext<FieldInputFormSlice>()

    const inputType = watch(`${prefix}inputType`) as FieldInputType
    const fieldPointer = watch(`${prefix}fieldPointer`) as string
    const isUsedAreaCode = watch(`${prefix}phoneNumber.isUsedAreaCode`) as boolean
    const dateRange = watch(`${prefix}date.range`) as DateRange
    const isRegular = watch(`${prefix}text.isRegular`) as boolean
    const calendarRangeDsId = watch(`${prefix}calendar.rangeDsId`) as string
    const calendarRange = watch(`${prefix}calendar.range`) as DateRange
    const required = watch(`${prefix}required`) as boolean
    const isOpened = watch(`${prefix}phoneNumber.isOpened`) as boolean
    const watchedFileLimitType = (watch(`${prefix}file.fileLimitType`) as FIELD_TYPE_LIMIT_ENUM | undefined) ?? FIELD_TYPE_LIMIT_ENUM.ALL

    const isParentNotSubForm = mode !== 'subForm'

    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSource = useDataSource(appId, envId, pointer)

    const pageId = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => s?.pageId || '', [])
    )
    const pageVariables = useAtomData(pageVariableSchemasFamily(pageId))
    const pagePathParameter = useAtomData(pagePathParameterFamily(pageId))
    const pageQueryParameter = useAtomData(pageQueryParameterFamily(pageId))
    const pageStateParameter = useAtomData(pageStatesParameterFamily(pageId))
    const availableFieldBlocksInfo = useDeferredValue(useAtomData(availableFieldBlocksAtom))
    const availableFilterBlocksInfo = useDeferredValue(useAtomData(availableFilterBlocksAtom))
    const findUseObject = useFindUseObjectContext()
    const dataSourceList = useDataSourceList(appId, envId)

    const { run: setBlockHighlight } = useAtomAction(blockHighlightAtom)
    const handleMouseEnter = useCallback(
        (id: string) => {
            setBlockHighlight([id])
            document.querySelector(`div[data-node-id="${id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        },
        [setBlockHighlight]
    )
    const handleMouseLeave = useCallback(() => {
        setBlockHighlight([])
    }, [setBlockHighlight])

    const calendarDataSource = useDataSource(appId, envId, calendarRangeDsId)
    const { schema: calendarSchema = {}, viewOptions: { tableProps: calendarTableProps = [] } = {} } = calendarDataSource || {}
    const calendarColumns = getViewColumns({
        tableProps: calendarTableProps,
        schema: calendarSchema
    })
    const calendarPrimaryField = useMemo(() => {
        if (!calendarDataSource) {
            return calendarColumns[0]?.fieldId
        }
        const field = getAppointField(calendarDataSource, 'ID')
        return field?.id || calendarColumns[0]?.fieldId
    }, [calendarColumns, calendarDataSource])

    const calendarDateFieldOptions = getFieldOptions(calendarSchema, calendarTableProps).filter(v => v.innerType === 'DATE')

    const field = dataSource?.schema?.[fieldPointer]

    const isShowRequired = field?.type !== 'formula'
    const isAvatarFile = inputType === 'file' && (field?.id ?? '').toUpperCase() === 'AVATAR'
    const isVideoFile = inputType === 'file' && field?.type === 'video'
    const { quota } = useSpaceQuotaContext()
    const fileMaxUploadSizeBytes = quota?.uploadFileLimitSize ?? defaultFileMaxUploadSize
    const fileMaxUploadSizeMB = getFileSizeToMB(fileMaxUploadSizeBytes)

    const isFileRuleAllLocked = isAvatarFile
    const isFileTypeLocked = isAvatarFile || isVideoFile
    const lockedFileRule = AVATAR_FILE_INPUT_LIMITS
    const uiFileLimitType = isAvatarFile ? FIELD_TYPE_LIMIT_ENUM.TYPE : isVideoFile ? FIELD_TYPE_LIMIT_ENUM.SUFFIX : watchedFileLimitType

    return (
        <Group
            styles={{
                root: {
                    padding: '12px 0!important'
                },
                collapse: {
                    padding: '0!important'
                }
            }}
            label="校验设置"
        >
            <SCxContainer>
                {isShowRequired && (
                    <Controller
                        name={`${prefix}required`}
                        render={({ field }) => (
                            <Required
                                label={inputType === 'checkbox' ? '必须勾选' : '必填'}
                                value={field.value}
                                onChange={v => {
                                    field.onChange(v)
                                    setValue(`${prefix}isRequiredWhenHidden`, v)
                                }}
                            />
                        )}
                    />
                )}
                {isShowRequired && isParentNotSubForm && required && (
                    <Controller
                        name={`${prefix}isRequiredWhenHidden`}
                        render={({ field }) => <Required label="当输入框隐藏时必填不生效" value={field.value} onChange={field.onChange} />}
                    />
                )}
                {inputType === 'text' && (
                    <>
                        <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                            <Text>字数约束</Text>
                            <Flex justifyContent="space-between" alignItems="center" style={{ width: 180 }}>
                                <Controller
                                    name={`${prefix}text.minFontNumber`}
                                    control={control}
                                    render={({ field }) => (
                                        <Input
                                            style={{ width: 80 }}
                                            type="number"
                                            max={1000}
                                            value={field.value}
                                            onChange={field.onChange}
                                            onBlur={e => {
                                                const newValue = e.target.value
                                                if (newValue === '') {
                                                    return
                                                }
                                                if (Number(newValue) < 1) {
                                                    field.onChange(1)
                                                }
                                                const maxFontNumber = getValues(`${prefix}text.maxFontNumber`)
                                                if (maxFontNumber === '') {
                                                    return
                                                }
                                                if (Number(newValue) > Number(maxFontNumber)) {
                                                    setValue('text.maxFontNumber', Number(newValue) + 1)
                                                }
                                            }}
                                        />
                                    )}
                                />
                                -
                                <Controller
                                    name={`${prefix}text.maxFontNumber`}
                                    control={control}
                                    render={({ field }) => (
                                        <Input
                                            style={{ width: 80 }}
                                            type="number"
                                            min={1}
                                            value={field.value}
                                            onChange={field.onChange}
                                            onBlur={e => {
                                                const newValue = e.target.value
                                                if (Number(newValue) < 1 && newValue !== '') {
                                                    field.onChange(1)
                                                }
                                                const minFontNumber = getValues(`${prefix}text.minFontNumber`)
                                                if (minFontNumber === '' || newValue === '') {
                                                    return
                                                }

                                                if (Number(minFontNumber) > Number(newValue)) {
                                                    field.onChange(Number(minFontNumber) + 1)
                                                }
                                            }}
                                        />
                                    )}
                                />
                            </Flex>
                        </ListItem4ByecodeUi>
                        <Controller
                            control={control}
                            name={`${prefix}text.isRegular`}
                            render={({ field: { value, onChange } }) => (
                                <SwitchControl
                                    checked={value}
                                    label="使用正则校验"
                                    onChange={e => {
                                        onChange?.(e.currentTarget.checked)
                                    }}
                                />
                            )}
                        />
                        {isRegular && (
                            <>
                                <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                                    <PopoverQuestion
                                        type="regular"
                                        renderTarget={(iconEle, ref) => (
                                            <>
                                                <Flex gap={4} ref={ref} alignItems="center">
                                                    <Text>自定义正则</Text>
                                                    {iconEle}
                                                </Flex>
                                                <Input style={{ width: 180 }} {...register(`${prefix}text.regular`)} />
                                            </>
                                        )}
                                    />
                                </ListItem4ByecodeUi>

                                <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                                    <Text>错误时提示</Text>
                                    <Input style={{ width: 180 }} {...register(`${prefix}text.regularErrorLabel`)} />
                                </ListItem4ByecodeUi>
                            </>
                        )}
                    </>
                )}
                {inputType === 'number' && (
                    <>
                        <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                            <Text>最小值</Text>
                            <Input style={{ width: 180 }} type="number" {...register(`${prefix}number.min`)} />
                        </ListItem4ByecodeUi>
                        <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                            <Text>最大值</Text>
                            <Input style={{ width: 180 }} type="number" {...register(`${prefix}number.max`)} />
                        </ListItem4ByecodeUi>
                    </>
                )}
                {inputType === 'date' && (
                    <>
                        <Controller
                            name={`${prefix}date.range`}
                            render={({ field }) => (
                                <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                                    <Text>可选日期</Text>
                                    <Select
                                        value={field.value}
                                        style={{ width: 180 }}
                                        options={DATE_RANGE_OPTIONS}
                                        onChange={field.onChange}
                                    />
                                </ListItem4ByecodeUi>
                            )}
                        />
                        {dateRange === 'AFTER_CUSTOM' && (
                            <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                                <Text>多少天</Text>
                                <Controller
                                    control={control}
                                    name={`${prefix}date.customDays`}
                                    render={({ field }) => (
                                        <Input
                                            style={{ width: 180 }}
                                            type="number"
                                            min={0}
                                            step="1"
                                            value={field.value}
                                            onChange={e => field.onChange(Math.round(Number(e.target.value)))}
                                        />
                                    )}
                                />
                            </ListItem4ByecodeUi>
                        )}
                    </>
                )}

                {inputType === 'file' && (
                    <>
                        <Controller
                            control={control}
                            name={`${prefix}file.enableSortable`}
                            render={({ field: { value, onChange } }) => (
                                <SwitchControl
                                    checked={value ?? true}
                                    label="允许拖拽排序"
                                    onChange={e => {
                                        onChange?.(e.currentTarget.checked)
                                    }}
                                />
                            )}
                        />
                        <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                            <Text>文件数量限制</Text>
                            <Controller
                                name={`${prefix}file.uploadLengthMaxLimit`}
                                control={control}
                                render={({ field }) => (
                                    <Input
                                        style={{ width: 180 }}
                                        type="number"
                                        min={0}
                                        placeholder="最多"
                                        disabled={isFileRuleAllLocked}
                                        value={isFileRuleAllLocked ? lockedFileRule?.uploadLengthMaxLimit ?? 1 : field.value}
                                        onChange={field.onChange}
                                        onBlur={e => {
                                            if (isFileRuleAllLocked) {
                                                return
                                            }
                                            const newValue = e.target.value
                                            if (newValue === '') {
                                                return
                                            }
                                            let nextValue = Math.round(Number(newValue))
                                            if (Number.isNaN(nextValue)) {
                                                return
                                            }
                                            if (nextValue < 0) {
                                                nextValue = 0
                                            }

                                            const minLimit = getValues(`${prefix}uploadLengthMinLimit`)
                                            if (minLimit !== '' && minLimit !== undefined && minLimit !== null) {
                                                const minNum = Number(minLimit)
                                                if (!Number.isNaN(minNum) && nextValue < minNum) {
                                                    nextValue = minNum
                                                }
                                            }

                                            field.onChange(nextValue)
                                        }}
                                    />
                                )}
                            />
                        </ListItem4ByecodeUi>

                        <Controller
                            name={`${prefix}file.fileLimitType`}
                            control={control}
                            render={({ field }) => {
                                return (
                                    <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                                        <Text>文件类型</Text>
                                        <SegmentedControl
                                            style={{ width: 180 }}
                                            fullWidth
                                            disabled={isFileTypeLocked}
                                            value={uiFileLimitType}
                                            data={FILE_LIMIT_TYPE_OPTIONS}
                                            onChange={v => {
                                                if (isFileTypeLocked) {
                                                    return
                                                }
                                                const nextType = v as FIELD_TYPE_LIMIT_ENUM
                                                field.onChange(nextType)
                                                if (nextType === FIELD_TYPE_LIMIT_ENUM.ALL) {
                                                    setValue(`${prefix}file.fileType`, undefined)
                                                    setValue(`${prefix}file.fileSuffix`, undefined)
                                                    return
                                                }
                                                if (nextType === FIELD_TYPE_LIMIT_ENUM.TYPE) {
                                                    setValue(`${prefix}file.fileSuffix`, undefined)
                                                    const existing = getValues(`${prefix}file.fileType`)
                                                    if (!existing) {
                                                        setValue(`${prefix}file.fileType`, FILE_TYPE_CATEGORY_ENUM.IMAGE)
                                                    }
                                                    return
                                                }
                                                if (nextType === FIELD_TYPE_LIMIT_ENUM.SUFFIX) {
                                                    setValue(`${prefix}file.fileType`, undefined)
                                                }
                                            }}
                                        />
                                    </ListItem4ByecodeUi>
                                )
                            }}
                        />

                        {uiFileLimitType === FIELD_TYPE_LIMIT_ENUM.TYPE && (
                            <Controller
                                name={`${prefix}file.fileType`}
                                control={control}
                                render={({ field }) => (
                                    <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                                        <Text>类型</Text>
                                        <SegmentedControl
                                            style={{ width: 180 }}
                                            size="xs"
                                            labelStyle={{ overflow: 'visible' }}
                                            disabled={isFileTypeLocked}
                                            value={field.value ?? FILE_TYPE_CATEGORY_ENUM.IMAGE}
                                            data={FILE_TYPE_CATEGORY_OPTIONS}
                                            onChange={v => field.onChange(v)}
                                        />
                                    </ListItem4ByecodeUi>
                                )}
                            />
                        )}

                        {uiFileLimitType === FIELD_TYPE_LIMIT_ENUM.SUFFIX && (
                            <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                                <Text>后缀</Text>
                                {isVideoFile ? (
                                    <Input style={{ width: 180 }} disabled value={VIDEO_FILE_INPUT_LIMITS.fileSuffix ?? ''} />
                                ) : (
                                    <Input
                                        style={{ width: 180 }}
                                        placeholder="例如“xls,doc,pdf”"
                                        disabled={isFileTypeLocked}
                                        {...register(`${prefix}file.fileSuffix`)}
                                    />
                                )}
                            </ListItem4ByecodeUi>
                        )}

                        <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                            <Text>单个文件不能大于</Text>
                            <Flex justifyContent="flex-end" alignItems="center" gap={8} style={{ width: 180 }}>
                                <Controller
                                    name={`${prefix}file.fileSizeLimit`}
                                    control={control}
                                    render={({ field }) => (
                                        <Slider
                                            min={0.1}
                                            max={fileMaxUploadSizeMB}
                                            step={5}
                                            onChange={field.onChange}
                                            value={(field.value ?? 5) as number}
                                            style={{ width: 140 }}
                                            inputProps={{
                                                onFocus: e => {
                                                    e.target.select()
                                                }
                                            }}
                                        />
                                    )}
                                />
                                <Text color="var(--color-gray-500)">MB</Text>
                            </Flex>
                        </ListItem4ByecodeUi>
                    </>
                )}

                {inputType === 'calendar' && (
                    <>
                        <Controller
                            name={`${prefix}calendar.range`}
                            render={({ field }) => (
                                <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                                    <Text>可选日期</Text>
                                    <Select
                                        value={field.value}
                                        style={{ width: 180 }}
                                        options={CALENDAR_RANGE_OPTIONS}
                                        onChange={field.onChange}
                                    />
                                </ListItem4ByecodeUi>
                            )}
                        />
                        {calendarRange === 'AFTER_CUSTOM' && (
                            <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                                <Text>多少天</Text>
                                <Controller
                                    control={control}
                                    name={`${prefix}calendar.customDays`}
                                    render={({ field }) => (
                                        <Input
                                            style={{ width: 180 }}
                                            type="number"
                                            min={0}
                                            step="1"
                                            value={field.value}
                                            onChange={e => field.onChange(Math.round(Number(e.target.value)))}
                                        />
                                    )}
                                />
                            </ListItem4ByecodeUi>
                        )}

                        {calendarRange === 'DATASOURCE_CUSTOM' && (
                            <>
                                <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                                    <Text>数据表</Text>
                                    <Controller
                                        control={control}
                                        name={`${prefix}calendar.rangeDsId`}
                                        render={({ field }) => (
                                            <SelectDataSource
                                                placeholder="请选择数据表"
                                                size="md"
                                                value={field.value}
                                                withinPortal
                                                onChange={field.onChange}
                                                dataSourceList={dataSourceList}
                                            />
                                        )}
                                    />
                                </ListItem4ByecodeUi>
                                <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                                    <Text>匹配的日期字段</Text>
                                    <Controller
                                        control={control}
                                        name={`${prefix}calendar.rangeFieldId`}
                                        render={({ field }) => (
                                            <FieldSelect options={calendarDateFieldOptions} value={field.value} onChange={field.onChange} />
                                        )}
                                    />
                                </ListItem4ByecodeUi>
                                <Controller
                                    name={`${prefix}calendar.rangeFilter`}
                                    control={control}
                                    render={({ field: { onChange, value } }) => {
                                        const filter = value
                                        const filtersLength = filter?.expression?.conditions?.length || 0
                                        const isHighLight = getIsFindUseInFilter({
                                            findUseObject,
                                            data: filter,
                                            currentDsId: calendarRangeDsId
                                        })
                                        return (
                                            <ListItemMenu
                                                width="auto"
                                                highlighting={isHighLight}
                                                enableHover={false}
                                                label="筛选数据"
                                                positionDependencies={[filter]}
                                                value={Boolean(filtersLength) && `${String(filtersLength)} 条筛选项`}
                                                popupContent={
                                                    <ViewFieldFilterConfigure
                                                        dataSource={calendarDataSource}
                                                        dataSourceList={dataSourceList}
                                                        pagePathParameter={pagePathParameter}
                                                        pageQueryParameter={pageQueryParameter}
                                                        pageVariables={pageVariables}
                                                        pageStateParameter={pageStateParameter}
                                                        availableFieldBlocksInfo={availableFieldBlocksInfo}
                                                        availableFilterBlocksInfo={availableFilterBlocksInfo}
                                                        columns={calendarColumns}
                                                        primaryField={calendarPrimaryField}
                                                        filter={filter}
                                                        paramsMode={FilterMode.CUSTOM}
                                                        noSettingInnerType={notDataSettingInnerTypes}
                                                        onFilter={onChange}
                                                        onMouseEnter={handleMouseEnter}
                                                        onMouseLeave={handleMouseLeave}
                                                    />
                                                }
                                            />
                                        )
                                    }}
                                />
                            </>
                        )}
                    </>
                )}
                {PURE_TEXT_INPUT.includes(inputType) && (
                    <Controller
                        name={`${prefix}noRepeat`}
                        render={({ field: { value, onChange } }) => <Repeat value={value} onChange={onChange} />}
                    />
                )}
                {inputType === 'phoneNumber' && mode === 'form' && (
                    <>
                        <ListItem4ByecodeUi enableHover justifyContent="space-between" alignItems="center">
                            <PopoverQuestion
                                type="smsVerification"
                                renderTarget={(node, ref) => (
                                    <>
                                        <Flex gap={4} alignItems="center" ref={ref}>
                                            <Text>短信验证</Text>
                                            {node}
                                        </Flex>
                                        <Controller
                                            control={control}
                                            name={`${prefix}phoneNumber.isOpened`}
                                            render={({ field }) => (
                                                <Switch
                                                    checked={field.value}
                                                    disabled={isUsedAreaCode}
                                                    onChange={e => {
                                                        field.onChange?.(e.currentTarget.checked)
                                                    }}
                                                />
                                            )}
                                        />
                                    </>
                                )}
                            />
                        </ListItem4ByecodeUi>
                        {isOpened && (
                            <ListItem4ByecodeUi>
                                <AppSmsCard />
                            </ListItem4ByecodeUi>
                        )}
                    </>
                )}
            </SCxContainer>
        </Group>
    )
}
